import { Button, Card, Descriptions, Form, Modal, Space, Spin, Typography } from 'antd';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import ILoanDrawdown from '~Api/Loan/ILoanDrawdown';
import LoanStatusEnum from '~Api/Loan/LoanStatusEnum';
import { IGlobalState } from '~reducer';
import ModalStateEnum from '~UI/ModalStateEnum';
import LoanDrawdownStatusEnum from '~Api/Loan/LoanDrawdownStatusEnum';
import { useParams } from 'react-router-dom';
import {
    loanDrawdownGetAction,
    loanDrawdownInternalCommentsAction,
    loanGetAction,
    loanWarehouseLoansListAction,
} from '~Loans/actions';
import {
    loanDrawdownItemsSelector,
    loanDrawdownSelector,
    loanSelector,
    loanWarehouseLoansSelector,
} from '~Loans/selectors';
import ILoan from '~Api/Loan/ILoan';
import Layout from './Layout';
import IAuthUser from '~Auth/IAuthUser';
import { authCurrentUserSelector } from '~Auth/selectors';
import PermissionsEnum from '~Api/Administrator/PermissionsEnum';
import DrawdownDisbursalCalculationModal from './DrawdownDisbursalCalculationModal';
import { IDictionary } from '~utilities/IDictionary';
import ILoanDrawdownItem from '~Api/Loan/ILoanDrawdownItem';
import _ from 'lodash';
import { currencyFormatter } from '~utilities/formatters';
import { getLoanDrawdownTotalToBeDisbursed } from '~Loans/utilities';
import PopoverWarning from '~UI/PopoverWarning';
import IWarehouse from '~Api/Warehouse/IWarehouse';
import { warehousesSelector } from '~Warehouses/selectors';
import { warehousesListAction } from '~Warehouses/actions';
import IWarehouseLoan from '~Api/Warehouse/IWarehouseLoan';
import WysiwygEditor from '~UI/WysiwygEditor';
import { EditOutlined } from '@ant-design/icons';

const validateAndDisplayBalanceStatuses: LoanDrawdownStatusEnum[] = [
    LoanDrawdownStatusEnum.QsReceived,
    LoanDrawdownStatusEnum.PendingApproval,
    LoanDrawdownStatusEnum.Approved,
    LoanDrawdownStatusEnum.DisbursalInstructed,
];

interface ITableErrors {
    approvedDrawdownAmount?: string;
    retainedContingencyAmount?: string;
    retainedFundsAmount?: string;
    totalToBeDisbursed?: string;
}

interface IItemErrors {
    amount?: string;
    description?: string;
}

export default function Overview(): ReactElement {
    const { loanDrawdownUuid, loanUuid } = useParams<{ loanUuid: string; loanDrawdownUuid: string }>();

    const [ internalCommentsModalState, setInternalCommentsModalState ] = useState<ModalStateEnum>(ModalStateEnum.Off);
    const [ isDisbursalCalculationModalOpen, setIsDisbursalCalculationModalOpen ] = useState<ModalStateEnum>(ModalStateEnum.Off);

    const currentUser: IAuthUser = useSelector(authCurrentUserSelector);
    const drawdown: ILoanDrawdown = useSelector((state: IGlobalState): ILoanDrawdown => loanDrawdownSelector(state, loanDrawdownUuid));
    const loan: ILoan = useSelector((state: IGlobalState): ILoan => loanSelector(state, loanUuid));
    const items: IDictionary<ILoanDrawdownItem> = useSelector((state: IGlobalState): IDictionary<ILoanDrawdownItem> => loanDrawdownItemsSelector(state, loanDrawdownUuid));
    const warehouses: IDictionary<IWarehouse> = useSelector(warehousesSelector);
    const loanWarehouseLoans: IDictionary<IWarehouseLoan> = useSelector((state: IGlobalState): IDictionary<IWarehouseLoan> => loanWarehouseLoansSelector(state, loanUuid));

    const dispatch: Dispatch = useDispatch();
    const hasWarehousePermission: boolean = currentUser.permissions.includes(PermissionsEnum.Warehouses);

    useEffect((): void => {
        if (!drawdown || !items) {
            dispatch(loanDrawdownGetAction(loanDrawdownUuid));
        }
    }, [
        dispatch,
        drawdown,
        items,
        loanDrawdownUuid,
    ]);

    useEffect((): void => {
        if (!loan) {
            dispatch(loanGetAction(loanUuid));
        }
    }, [
        dispatch,
        loan,
        loanUuid,
    ]);

    useEffect((): void => {
        if (!warehouses && hasWarehousePermission) {
            dispatch(warehousesListAction());
        }
    }, [
        dispatch,
        hasWarehousePermission,
        warehouses,
    ]);

    useEffect((): void => {
        if (!loanWarehouseLoans && hasWarehousePermission) {
            dispatch(loanWarehouseLoansListAction(loanUuid));
        }
    }, [
        dispatch,
        hasWarehousePermission,
        loanUuid,
        loanWarehouseLoans,
    ]);

    const onClickDisbursalCalculation: () => void = useCallback((): void => {
        setIsDisbursalCalculationModalOpen(ModalStateEnum.Open);
    }, []);

    const onCloseDisbursalCalculationModal: () => void = useCallback((): void => {
        setIsDisbursalCalculationModalOpen(ModalStateEnum.Closed);
    }, []);

    const onChangeInternalComments: (internalComments: string) => void = useCallback((internalComments: string): void => {
        dispatch(loanDrawdownInternalCommentsAction(loanDrawdownUuid, internalComments));
    }, [
        dispatch,
        loanDrawdownUuid,
    ]);

    const onClickEditInternalComments: () => void = useCallback((): void => {
        setInternalCommentsModalState(ModalStateEnum.Open);
    }, []);

    const onCloseInternalCommentsModal: () => void = useCallback((): void => {
        setInternalCommentsModalState(ModalStateEnum.Closed);
    }, []);

    if (!drawdown || !loan || (hasWarehousePermission && !loanWarehouseLoans) || (hasWarehousePermission && !warehouses)) {
        return (
            <Layout loanUuid={loanUuid} loanDrawdownUuid={loanDrawdownUuid} section='overview'>
                <Typography.Title level={2}>Overview</Typography.Title>
                <Spin />
            </Layout>
        );
    }

    const drawdownAmountBlock: string = drawdown.approvedDrawdownAmount ? currencyFormatter.format(drawdown.approvedDrawdownAmount) : '-';

    const retainedFundsBalance: number = loan.retainedFunds - (drawdown.retainedFundsAmount ?? 0);
    const retainedFundsBalanceBlock: ReactElement = validateAndDisplayBalanceStatuses.includes(drawdown.status) && (
        <span className='remaining-balance'>({currencyFormatter.format(retainedFundsBalance)} Remaining)</span>
    );
    const retainedFundsAmountBlock: string = drawdown.retainedFundsAmount !== null ? currencyFormatter.format(drawdown.retainedFundsAmount) : '-';

    const retainedContingencyBalance: number = loan.retainedContingency - (drawdown.retainedContingencyAmount ?? 0);
    const retainedContingencyBalanceBlock: ReactElement = validateAndDisplayBalanceStatuses.includes(drawdown.status) && (
        <span className='remaining-balance'>({currencyFormatter.format(retainedContingencyBalance)} Remaining)</span>
    );
    const retainedContingencyAmountBlock: string = drawdown.retainedContingencyAmount !== null ? currencyFormatter.format(drawdown.retainedContingencyAmount) : '-';

    const itemsBlock: ReactElement[] = _.map(_.sortBy(items, [ 'sortOrder' ]), (item: ILoanDrawdownItem): ReactElement => {
        const itemErrors: IItemErrors = {};
        if (validateAndDisplayBalanceStatuses.includes(drawdown.status)) {
            if (!item.description) {
                itemErrors.description = 'Please enter a description';
            }
            if (item.amount === null) {
                itemErrors.amount = 'Please enter an amount';
            }
        }

        const itemLabelBlock: ReactElement = (
            <PopoverWarning
                help={itemErrors.description}
                validateStatus={itemErrors.description && 'error'}
            >
                {item.description ? item.description : '-'}
            </PopoverWarning>
        );

        const itemAmountBlock: ReactElement = (
            <PopoverWarning
                help={itemErrors.amount}
                validateStatus={itemErrors.amount && 'error'}
            >
                {item.amount !== null ? currencyFormatter.format(item.amount) : '-'}
            </PopoverWarning>
        );

        return <Descriptions.Item className='fee' label={itemLabelBlock}>{itemAmountBlock}</Descriptions.Item>;
    });

    const totalToBeDisbursed: number = getLoanDrawdownTotalToBeDisbursed(drawdown, items);

    const disbursalTotal: number = (drawdown.retainedContingencyAmount ?? 0) + (drawdown.retainedFundsAmount ?? 0);
    const tableErrors: ITableErrors = {};

    if (validateAndDisplayBalanceStatuses.includes(drawdown.status)) {
        if (drawdown.approvedDrawdownAmount === null) {
            tableErrors.approvedDrawdownAmount = 'Please enter an amount';
        }

        if (_.round(disbursalTotal, 2) !== _.round(totalToBeDisbursed, 2)) {
            tableErrors.totalToBeDisbursed = `Disbursal Total does not satisfy the Total To Be Disbursed of ${currencyFormatter.format(totalToBeDisbursed)}`;
        }

        if (drawdown.retainedFundsAmount > loan.retainedFunds) {
            tableErrors.retainedFundsAmount = 'The amount exceeds the available funds';
        }
        if (drawdown.retainedContingencyAmount > loan.retainedContingency) {
            tableErrors.retainedContingencyAmount = 'The amount exceeds the available funds';
        }
    }

    const disbursalCalculationModalBlock: ReactElement = ModalStateEnum.Off !== isDisbursalCalculationModalOpen && (
        <DrawdownDisbursalCalculationModal
            loanUuid={loanUuid}
            loanDrawdownUuid={loanDrawdownUuid}
            isOpen={ModalStateEnum.Open === isDisbursalCalculationModalOpen}
            onCancel={onCloseDisbursalCalculationModal}
        />
    );

    const editDisbursalButtonBlock: ReactElement = (
        LoanStatusEnum.ClosedObligationsMet !== loan.status
        && LoanDrawdownStatusEnum.QsReceived === drawdown.status
        && (
            <Button
                className='edit-disbursal'
                onClick={onClickDisbursalCalculation}
            >
                Edit
            </Button>
        )
    );

    const disbursalTableBlock: ReactElement = [
        LoanDrawdownStatusEnum.QsReceived,
        LoanDrawdownStatusEnum.PendingApproval,
        LoanDrawdownStatusEnum.Approved,
        LoanDrawdownStatusEnum.DisbursalInstructed,
        LoanDrawdownStatusEnum.Processed,
    ].includes(drawdown.status) && (
        <>
            {editDisbursalButtonBlock}
            <Typography.Title className='disbursal-title' level={3}>Disbursal</Typography.Title>
            <Descriptions bordered={true} column={1}>
                <Descriptions.Item label='Approved Drawdown Amount'>
                    <PopoverWarning
                        help={tableErrors.approvedDrawdownAmount}
                        validateStatus={tableErrors.approvedDrawdownAmount && 'error'}
                    >
                        {drawdownAmountBlock}
                    </PopoverWarning>
                </Descriptions.Item>
                <Descriptions.Item span={2} className='fees-title' label='Fees'>&nbsp;</Descriptions.Item>
                {itemsBlock}
                <Descriptions.Item className='disbursal-total' label='Total to be Disbursed'>
                    <PopoverWarning
                        help={tableErrors.totalToBeDisbursed}
                        validateStatus={tableErrors.totalToBeDisbursed && 'error'}
                    >
                        {currencyFormatter.format(totalToBeDisbursed)}
                    </PopoverWarning>
                </Descriptions.Item>
                <Descriptions.Item span={2} className='disbursed-from-title' label='Disbursed From'>&nbsp;</Descriptions.Item>
                <Descriptions.Item className='retained-funds' label='Retained Funds'>
                    <PopoverWarning
                        help={tableErrors.retainedFundsAmount}
                        validateStatus={tableErrors.retainedFundsAmount && 'error'}
                    >
                        {retainedFundsAmountBlock}
                        {retainedFundsBalanceBlock}
                    </PopoverWarning>
                </Descriptions.Item>
                <Descriptions.Item className='retained-contingency' label='Retained Contingency'>
                    <PopoverWarning
                        help={tableErrors.retainedContingencyAmount}
                        validateStatus={tableErrors.retainedContingencyAmount && 'error'}
                    >
                        {retainedContingencyAmountBlock}
                        {retainedContingencyBalanceBlock}
                    </PopoverWarning>
                </Descriptions.Item>
            </Descriptions>
        </>
    );

    const internalCommentsEditModalBlock: JSX.Element = ModalStateEnum.Off !== internalCommentsModalState && (
        <Modal
            cancelText='Close'
            className='loan-drawdown-internal-comments-modal'
            okButtonProps={{ style: { display: 'none' } }}
            onCancel={onCloseInternalCommentsModal}
            open={internalCommentsModalState === ModalStateEnum.Open}
            title='Internal Comments'
            width={1000}
        >
            <Form.Item className='internal-comments'>
                <WysiwygEditor
                    bold={true}
                    italic={true}
                    link={true}
                    onChange={onChangeInternalComments}
                    orderedList={true}
                    underline={true}
                    unorderedList={true}
                    value={drawdown.internalComments}
                />
            </Form.Item>
        </Modal>
    );

    const editInternalCommentsButtonBlock: ReactElement = LoanStatusEnum.ClosedObligationsMet !== loan.status && (
        <Button
            className='edit'
            onClick={onClickEditInternalComments}
            type='link'
        >
            <EditOutlined />
        </Button>
    );

    return (
        <Layout loanUuid={loanUuid} section='overview' loanDrawdownUuid={loanDrawdownUuid}>
            <Typography.Title level={2}>Overview</Typography.Title>
            <Typography.Title className='internal-comments' level={3}>
                <Space>
                    <>Internal Comments</>
                    {editInternalCommentsButtonBlock}
                </Space>
            </Typography.Title>
            <Card>
                <div dangerouslySetInnerHTML={{ __html: drawdown.internalComments || '-' }} />
            </Card>
            {internalCommentsEditModalBlock}
            {disbursalTableBlock}
            {disbursalCalculationModalBlock}
        </Layout>
    );
}